import type { SalesChecklist } from "@expert/contentful";
import type { WorkspaceFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";

import { useFeatures } from "@expert/features";
import { useCallback, useEffect, useRef, useState } from "react";

import { DynamicSmartOffer } from "../DynamicSmartOffer";
import { useDynamicSmartOfferStore } from "../store/useDynamicSmartOfferStore";

interface Dispatcher {
    dispatchBusinessEvent: (event: string, payload?: Record<string, unknown>) => void | Promise<void>;
}

const FETCH_DELAY_MS = 2000;

export function useDynamicSmartOffer({
    sessionId,
    salesChecklist,
    partner,
    dispatcher,
    logger,
}: {
    sessionId: string;
    salesChecklist: SalesChecklist;
    partner: string;
    dispatcher: Dispatcher;
    logger: Logger;
}) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [isPending, setIsPending] = useState(false);
    const [data, setData] = useState<DynamicSmartOffer>(new DynamicSmartOffer("", []));

    const {
        features: { isDynamicSmartOfferEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const isEnabled = isDynamicSmartOfferEnabled && hasCoreBenefitsWithDynamicContent(salesChecklist);

    // Add a delay to simulate a fetch
    const fetch = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsPending(true);
        timerRef.current = setTimeout(() => {
            const latestData = useDynamicSmartOfferStore.getState().getSession(sessionId).latestData;
            logger.info({ sessionId, isValid: latestData.isValid() }, "Dynamic smart offer timer resolved");
            setData(latestData);
            setIsPending(false);
        }, FETCH_DELAY_MS);
    }, [sessionId, logger]);

    useEffect(() => {
        fetch();
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [fetch]);

    const storeLatestData = useDynamicSmartOfferStore((s) => s.getSession(sessionId).latestData);

    const showLatest = useCallback(() => {
        void dispatcher.dispatchBusinessEvent("DynamicSmartOfferShowLatestClicked", { sessionId, partner });
        fetch();
    }, [fetch, sessionId, partner, dispatcher]);

    const isAwaitingData = !isPending && !data.isValid();
    const hasNewerData = !isPending && storeLatestData.isValid() && !storeLatestData.equals(data);

    // When awaiting initial data and an event arrives, auto-fetch (with the standard delay).
    useEffect(() => {
        if (isAwaitingData && hasNewerData) {
            logger.info({ sessionId }, "Dynamic smart offer auto-fetch triggered");
            fetch();
        }
    }, [isAwaitingData, hasNewerData, fetch, logger, sessionId]);

    // Fire once when dynamic content is first rendered — i.e., when data transitions from
    // invalid to valid after the pending delay. This confirms the expert actually saw the
    // generated offer, not just that we received the WS event.
    const hasDispatchedRef = useRef(false);
    useEffect(() => {
        if (!hasDispatchedRef.current && data.isValid()) {
            hasDispatchedRef.current = true;
            void dispatcher.dispatchBusinessEvent("DynamicSmartOfferShown", { sessionId, partner });
        }
    }, [data, sessionId, partner, dispatcher]);

    return { data, isPending, isAwaitingData, isEnabled, showLatest, hasNewerData };
}

function hasCoreBenefitsWithDynamicContent(salesChecklist: SalesChecklist) {
    return salesChecklist.checklistSections.some((s) => s.sectionType === "coreBenefits" && s.enableDynamicContent);
}
